import { format, fromZonedTime } from 'date-fns-tz';
import { orderBy as _orderBy } from 'lodash-es';

import useTranslate from '@hooks/useTranslate';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { IEquipmentsLogItem } from '../model/equipmentsLogList';
import { useEquipmentsLogListInfiniteQuery } from '../services/useEquipmentsLogListInfiniteQuery';

const useEquipmentsLog = ({ searchValue }: { searchValue: string }) => {
  const { t } = useTranslation();
  const { translateField } = useTranslate();
  const { data: profileData } = useUserProfileQuery();

  const locationSelectValue = selectAppLocationSelectValue();

  const {
    data: equipmentsQuery,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
  } = useEquipmentsLogListInfiniteQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    search: searchValue,
  });

  const equipmentLogsData = equipmentsQuery?.pages?.flatMap((page) => page.list) ?? [];

  const getLogMessage = (log: IEquipmentsLogItem) => {
    const { action, old_data, new_data, message } = log;
    if (action === 'deactivate') {
      return t('Deactivate Equipment from {from} to {to}', {
        from: format(fromZonedTime(old_data, 'UTC'), profileData?.dateFormat?.date || ''),
        to: new_data
          ? format(fromZonedTime(new_data, 'UTC'), profileData?.dateFormat?.date || '')
          : t('indefinitely'),
      });
    }
    return message;
  };

  const selectEquipmentsLog = (equipmentsLog: IEquipmentsLogItem[]) =>
    equipmentsLog.map((log) => {
      return {
        created_at: log.created_at,
        message: getLogMessage(log),
        user_name: log.user_name,
        facility_offset: log.facilityOffset,
        equipment_name:
          translateField({
            item: log,
            translatesField: 'log_translates',
            defaultField: 'log_name',
          }) || translateField({ item: log, field: 'equipment_name' }),
      };
    });

  const equipmentsLog = selectEquipmentsLog(equipmentLogsData);

  return {
    fetchNextPage,
    isFetching,
    hasNextPage,
    isLoading,
    equipmentsLog,
  };
};

export default useEquipmentsLog;
